// Device pieces of the detail parity with the local app (APP-PARITY.md "Detail-Abgleich"): the backflush times, the page
// title with the machine's state, the °C scale of the last 20 minutes, the recovery of the last known settings (no
// backups: the cloud is the backup), the export, diagnosis and the resets.
import { findCommand } from '@brewcore/protocol';
import type { RecoveryResponse, RecoveryResult } from '@brewcore/app-contract';
import { reasonText } from '../i18n.js';
import type { ParamValue } from '../store.js';
import { actionView, type ActionView, type ScreenContext } from './context.js';
import { dateText, decimalsOf, formatNumber } from './format.js';
import { chipView } from './frame.js';

type RecoveryGroupResult = RecoveryResult['results'][number];

/** Pump and pause of one backflush cycle (the local app falls back to 5 s and 10 s). */
export const backflushTimes = (ctx: ScreenContext): { fill: number; pause: number } => ({
  fill: ctx.num('backflush.fill_time', 5),
  pause: ctx.num('backflush.flush_time', 10),
});

/**
 * The page title with the machine's state ("Bereit · Orione", D-G01; paintLive() of the local app): the state shows
 * in the browser tab and the app switcher without opening the app. Before the first snapshot only the name.
 */
export function documentTitle(ctx: ScreenContext): string {
  const name = ctx.view.info?.name ?? 'Brewcore';
  if (!ctx.view.info) return name;
  return `${chipView(ctx).text} · ${name}`;
}

/**
 * The °C scale of "Letzte 20 Minuten" (drawChart() of the local app, D-M05): whole steps of 5, 10 or 20 K around the
 * temperatures and setpoints shown, a gridline with its label at each step. 20–100 °C before any sample.
 */
export function historyAxis(samples: readonly { temp: number | null; target: number | null }[]): {
  lo: number;
  hi: number;
  ticks: number[];
} {
  const temps = samples
    .flatMap((s) => [s.temp, s.target])
    .filter((v): v is number => typeof v === 'number' && v > 0);
  if (!temps.length) return { lo: 20, hi: 100, ticks: [20, 40, 60, 80, 100] };
  const min = Math.min(...temps);
  const max = Math.max(...temps);
  const step = max - min > 40 ? 20 : max - min > 15 ? 10 : 5;
  const lo = Math.floor((min - 1) / step) * step;
  const hi = Math.ceil((max + 1) / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= hi + 1e-9; v += step) ticks.push(v);
  return { lo, hi, ticks };
}

// ------------------------------------------------------------------ recovery (FR-52), export (W18)

/**
 * The offer after a data loss or a continued coupling (Dominik 19.10.2026: the cloud is the backup): once, on the
 * machine tab. The owner takes the last known state over or keeps the machine's; members see that the owner decides.
 */
export interface RecoveryView {
  /** "Die Maschine hat ihre Einstellungen verloren. Aus der Cloud wiederherstellen?" */
  question: string;
  /** "Stand vom 18.10.2026 · 4 Änderungen, 1 davon geschützt" or "hat schon dieselben Einstellungen" */
  detail: string;
  canDecide: boolean;
  /** the values to take over, S values marked (they get their own question) */
  lines: RestoreLine[];
  take: string;
  keep: string;
}

export function recoveryView(
  ctx: ScreenContext,
  offer: Pick<RecoveryResponse, 'takenAt' | 'values'>,
): RecoveryView {
  const lines = restoreLines(ctx, offer);
  const protectedCount = lines.filter((l) => l.protected).length;
  const day = dateText(offer.takenAt, ctx.lang);
  return {
    question: ctx.tr('recoveryQuestion'),
    detail: lines.length
      ? ctx.tr(protectedCount ? 'recoveryDetailS' : 'recoveryDetail', {
          d: day,
          n: lines.length,
          s: protectedCount,
        })
      : ctx.tr('recoverySame', { d: day }),
    canDecide: ctx.isOwner,
    lines,
    take: ctx.tr(lines.length ? 'recoveryTake' : 'recoveryOk'),
    keep: ctx.tr('recoveryKeep'),
  };
}

/** "Daten exportieren": the whole history to use elsewhere, explicitly no backup (Dominik 19.10.2026). */
export function exportView(ctx: ScreenContext): { title: string; hint: string; json: string; csv: string } {
  return {
    title: ctx.tr('exportData'),
    hint: ctx.tr('exportInfo'),
    json: ctx.tr('exportJson'),
    csv: ctx.tr('exportCsv'),
  };
}

/** A value as the app shows it ("93,5 °C", "an", "Schließer"), for a value that is not the machine's current one. */
export function valueText(ctx: ScreenContext, id: string, v: ParamValue | undefined): string {
  const c = findCommand(ctx.catalog, id);
  if (v === undefined) return '–';
  if (typeof v === 'boolean') return ctx.tr(v ? 'on' : 'off');
  if (typeof v === 'string') return v.trim() ? v : '–';
  if (c?.type === 'enum') return c.options?.find((o) => o.value === v)?.label[ctx.lang] ?? String(v);
  return `${formatNumber(v, decimalsOf(c?.step), ctx.lang)}${c?.unit ? `\u00a0${c.unit}` : ''}`;
}

/** A value of the last known state that differs from the machine now, to take over (protected: protection class S). */
export interface RestoreLine {
  id: string;
  label: string;
  from: string;
  to: string;
  protected: boolean;
}

const same = (a: ParamValue | undefined, b: ParamValue | undefined): boolean =>
  typeof a === 'number' && typeof b === 'number' ? Math.abs(a - b) < 1e-9 : a === b;

/**
 * What taking the last known state over would change, in the order of the catalog: only writable values (not the API's
 * own), only where the machine now has another value.
 */
export function restoreLines(ctx: ScreenContext, snap: Pick<RecoveryResponse, 'values'>): RestoreLine[] {
  const values = snap.values as Record<string, ParamValue>;
  return ctx.catalog.commands
    .filter((c) => c.kind === 'set' && c.role !== 'system' && c.id in values)
    .filter((c) => !same(values[c.id], ctx.value(c.id)))
    .map((c) => ({
      id: c.id,
      label: c.label[ctx.lang],
      from: valueText(ctx, c.id, ctx.value(c.id)),
      to: valueText(ctx, c.id, values[c.id]),
      protected: c.protection === 'S',
    }));
}

/** The machine's refusals in words: "Bezugszeit: Ein Bezug läuft." */
export function restoreProblems(ctx: ScreenContext, results: readonly RecoveryGroupResult[]): string[] {
  return results
    .filter((r) => r.result !== 'done')
    .map((r) => {
      const labels = r.params.map((p) => findCommand(ctx.catalog, p)?.label[ctx.lang] ?? p).join(', ');
      const why = reasonText(ctx.lang, r.reason ?? (r.result === 'offline' ? 'offline' : r.result));
      return `${labels}: ${why}`;
    });
}

// ------------------------------------------------------------------ diagnosis (W19), resets (W21, W22)

/** "Diagnose" under Einstellungen → System (Dominik 19.10.2026: "irgendwo versteckt"); owner and members (FR-24). */
export function diagView(ctx: ScreenContext): {
  title: string;
  hint: string;
  log: string;
  crash: string;
  enabled: boolean;
} {
  return {
    title: ctx.tr('sDiag'),
    hint: ctx.tr('diagInfo'),
    log: ctx.tr('diagLog'),
    crash: ctx.tr('diagCrash'),
    enabled: ctx.controllable,
  };
}

/**
 * WLAN reset and factory reset (FR-24): owner only (members do not see them), fresh login (the API checks), each with
 * the explanation of what follows as its safety question.
 */
export function resetActions(ctx: ScreenContext): { wifi: ActionView; factory: ActionView } | null {
  if (!ctx.isOwner) return null;
  return {
    wifi: actionView(ctx, 'wifi.reset', {
      label: 'wifiReset',
      ask: { title: 'wifiResetTitle', text: 'wifiResetText', danger: true },
    }),
    factory: actionView(ctx, 'factory.reset', {
      label: 'factoryReset',
      ask: { title: 'factoryResetTitle', text: 'factoryResetText', danger: true },
    }),
  };
}
